import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useNavigation, ScrollRestoration } from "react-router";
import Navbar from "./Navbar";
import Loader from "../controls/Loader";
import Player from "../controls/Player";
import Tooltip from "../controls/Tooltip";
import AppModal from "../modals/AppModal";
import { useFeedback } from "../contexts/FeedbackContext";
import { AudioFilterProvider, useAudioFilter } from "../contexts/AudioFilterContext";

const initialDraftPlaylistMeta = {
  id: null,
  name: "",
  slug: "",
  description: "",
  published: false,
  cover_art_track_id: null,
};

const DRAFT_STORAGE_KEY = "draftPlaylistState";

const readStoredDraft = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredDraft = (state) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable or full; the in-memory draft still works
  }
};

const clearStoredDraft = () => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // Ignore
  }
};

const LayoutContent = ({ props, navigate }) => {
  const navigation = useNavigation();
  const [user, setUser] = useState(null);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [appModalContent, setAppModalContent] = useState(null);
  const [activePlaylist, setActivePlaylist] = useState([]);
  const [activeTrack, setActiveTrack] = useState(null);
  const [customPlaylist, setCustomPlaylist] = useState(null);
  const [draftPlaylist, setDraftPlaylist] = useState(() => readStoredDraft()?.tracks ?? []);
  const [draftPlaylistMeta, setDraftPlaylistMeta] = useState(() => ({
    ...initialDraftPlaylistMeta,
    ...(readStoredDraft()?.meta ?? {})
  }));
  const [isDraftPlaylistSaved, setIsDraftPlaylistSaved] = useState(() => readStoredDraft()?.saved ?? false);
  const [viewMode, setViewMode] = useState("grid");
  const [sortOption, setSortOption] = useState("desc");
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setNotice, setAlert } = useFeedback();
  const { isFilterLoading } = useAudioFilter();

  useEffect(() => {
    // OAuth failure
    if (props.alert) {
      setAlert(props.alert);
    // OAuth success
    } else if (props.jwt) {
      handleLogin({
        jwt: props.jwt,
        username: props.username,
        usernameUpdatedAt: props.usernameUpdatedAt,
        email: props.email,
        admin: props.admin === true || props.admin === "true",
      }, "Google login successful");
    } else {
      let jwt = "";
      let username = "";
      let email = "";
      let usernameUpdatedAt = "";
      let admin = "";

      if (typeof window !== "undefined") {
        jwt = localStorage.getItem("jwt");
        username = localStorage.getItem("username");
        usernameUpdatedAt = localStorage.getItem("usernameUpdatedAt");
        email = localStorage.getItem("email");
        admin = localStorage.getItem("admin");
      }

      if (jwt && username && email) {
        setUser({ jwt, username, usernameUpdatedAt, email, admin: admin === "true" });
      } else {
        setUser("anonymous");
      }
    }
  }, []);

  const handleLogin = (userData, message) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("jwt", userData.jwt);
      localStorage.setItem("username", userData.username);
      localStorage.setItem("usernameUpdatedAt", userData.usernameUpdatedAt);
      localStorage.setItem("email", userData.email);
      localStorage.setItem("admin", userData.admin ? "true" : "false");

      const redirectPath = localStorage.getItem("redirectAfterLogin") || "/";
      localStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath);
      setNotice(message);
    }
    setUser(userData);
  };

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("jwt");
      localStorage.removeItem("username");
      localStorage.removeItem("usernameUpdatedAt");
      localStorage.removeItem("email");
      localStorage.removeItem("admin");
    }
    navigate("/");
    setUser("anonymous");
    setNotice("Logged out successfully");
  };

  useEffect(() => {
    writeStoredDraft({ tracks: draftPlaylist, meta: draftPlaylistMeta, saved: isDraftPlaylistSaved });
  }, [draftPlaylist, draftPlaylistMeta, isDraftPlaylistSaved]);

  const resetDraftPlaylist = () => {
    setDraftPlaylist([]);
    setDraftPlaylistMeta(initialDraftPlaylistMeta);
    setIsDraftPlaylistSaved(false);
    clearStoredDraft();
  };

  const playTrack = (playlist, track, shouldAutoplay = true) => {
    setActivePlaylist(playlist);
    setActiveTrack(track);
    setShouldAutoplay(shouldAutoplay);
  };

  const openAppModal = (content) => {
    setAppModalContent(content);
    setIsAppModalOpen(true);
  };

  const closeAppModal = () => {
    setIsAppModalOpen(false);
    setAppModalContent(null);
  };

  return (
    <>
      <ScrollRestoration />
      <Tooltip />
      {(navigation.state === "loading" || isFilterLoading) && <Loader />}
      <Navbar user={user} handleLogout={handleLogout} />
      <main className={activeTrack ? "with-player" : ""}>
        <Outlet
          context={{
            ...props,
            user,
            setUser,
            handleLogin,
            handleLogout,
            activePlaylist,
            setActivePlaylist,
            customPlaylist,
            setCustomPlaylist,
            draftPlaylist,
            setDraftPlaylist,
            draftPlaylistMeta,
            setDraftPlaylistMeta,
            resetDraftPlaylist,
            isDraftPlaylistSaved,
            setIsDraftPlaylistSaved,
            activeTrack,
            playTrack,
            openAppModal,
            closeAppModal,
            viewMode,
            setViewMode,
            sortOption,
            setSortOption,
            isPlaying,
            isLoading
          }}
        />
      </main>
      <Player
        activePlaylist={activePlaylist}
        activeTrack={activeTrack}
        setActiveTrack={setActiveTrack}
        customPlaylist={customPlaylist}
        openAppModal={openAppModal}
        shouldAutoplay={shouldAutoplay}
        setShouldAutoplay={setShouldAutoplay}
        onPlayingChange={setIsPlaying}
        onLoadingChange={setIsLoading}
      />
      <AppModal
        isOpen={isAppModalOpen}
        onRequestClose={closeAppModal}
        modalContent={appModalContent}
      />
    </>
  );
};

const Layout = ({ props }) => {
  const navigate = useNavigate();

  return (
    <AudioFilterProvider navigate={navigate}>
      <LayoutContent props={props} navigate={navigate} />
    </AudioFilterProvider>
  );
};

export default Layout;
